import { useState } from "react";
import { Button, Card, DropdownField, InputField, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { ConfirmDialog } from "../ConfirmDialog";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import type {
  Entrypoint,
  EntrypointConfig,
  EntrypointStatus,
  EntrypointType,
  NativeEntrypointConfig,
  PhoneEntrypointConfig,
  WebEntrypointConfig,
} from "../../redux/reducers/entrypointSlice";
import type { Release } from "../../redux/reducers/releaseSlice";
import type { Environment } from "../../redux/reducers/environmentSlice";
import type { OrganizationPhoneNumber } from "../../redux/reducers/organizationPhoneNumberSlice";

const ENTRYPOINT_TYPE_ICON: Record<EntrypointType, string> = {
  phone: "phone",
  web: "language",
  native: "code",
};

const TYPE_OPTIONS = [
  { label: "Phone", value: "phone" },
  { label: "Web", value: "web" },
  { label: "Native", value: "native" },
];

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Disabled", value: "disabled" },
];

const DIRECTION_OPTIONS = [
  { label: "Inbound", value: "inbound" },
  { label: "Outbound", value: "outbound" },
  { label: "Both", value: "both" },
];

type EntrypointInput = {
  name: string;
  type: EntrypointType;
  status: EntrypointStatus;
  config: EntrypointConfig;
};

type FormState = {
  name: string;
  type: EntrypointType;
  status: EntrypointStatus;
  phoneNumber: string;
  friendlyName: string;
  direction: "inbound" | "outbound" | "both";
  allowedOrigins: string;
  clientId: string;
};

const emptyForm: FormState = {
  name: "",
  type: "phone",
  status: "active",
  phoneNumber: "",
  friendlyName: "",
  direction: "inbound",
  allowedOrigins: "",
  clientId: "",
};

const formFromEntrypoint = (entrypoint: Entrypoint): FormState => {
  const base: FormState = { ...emptyForm, name: entrypoint.name, type: entrypoint.type, status: entrypoint.status };
  if (entrypoint.type === "phone") {
    const c = entrypoint.config as PhoneEntrypointConfig;
    base.phoneNumber = c.phoneNumber ?? "";
    base.friendlyName = c.friendlyName ?? "";
    base.direction = c.direction ?? "inbound";
  } else if (entrypoint.type === "web") {
    const c = entrypoint.config as WebEntrypointConfig;
    base.allowedOrigins = (c.allowedOrigins ?? []).join(", ");
  } else if (entrypoint.type === "native") {
    const c = entrypoint.config as NativeEntrypointConfig;
    base.clientId = c.clientId ?? "";
  }
  return base;
};

const buildConfig = (form: FormState): EntrypointConfig => {
  if (form.type === "phone") {
    const config: PhoneEntrypointConfig = {
      phoneNumber: form.phoneNumber.trim(),
      direction: form.direction,
    };
    const friendly = form.friendlyName.trim();
    if (friendly) config.friendlyName = friendly;
    return config;
  }
  if (form.type === "web") {
    const allowedOrigins = form.allowedOrigins
      .split(",")
      .map((o) => o.trim())
      .filter(Boolean);
    return { allowedOrigins } satisfies WebEntrypointConfig;
  }
  return { clientId: form.clientId.trim() } satisfies NativeEntrypointConfig;
};

const isFormValid = (form: FormState): boolean => {
  if (!form.name.trim()) return false;
  if (form.type === "phone") return !!form.phoneNumber.trim();
  if (form.type === "native") return !!form.clientId.trim();
  return true;
};

const summarizeConfig = (entrypoint: Entrypoint): string | null => {
  if (entrypoint.type === "phone") {
    const c = entrypoint.config as PhoneEntrypointConfig;
    return c.friendlyName || c.phoneNumber || null;
  }
  if (entrypoint.type === "web") {
    const origins = (entrypoint.config as WebEntrypointConfig).allowedOrigins ?? [];
    if (origins.length === 0) return null;
    return origins.length === 1 ? origins[0] : `${origins[0]} (+${origins.length - 1} more)`;
  }
  if (entrypoint.type === "native") {
    return (entrypoint.config as NativeEntrypointConfig).clientId || null;
  }
  return null;
};

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

type EntrypointFormFieldsProps = {
  form: FormState;
  setForm: (next: FormState) => void;
  phoneNumberOptions: { label: string; value: string }[];
};

const EntrypointFormFields = ({ form, setForm, phoneNumberOptions }: EntrypointFormFieldsProps) => {
  const update = (patch: Partial<FormState>) => setForm({ ...form, ...patch });
  return (
    <>
      <InputField
        label="Name"
        value={form.name}
        onChange={(v: string) => update({ name: v })}
        placeholder="e.g. main support line"
      />
      <View className="flex flex-row gap-3">
        <View className="flex-1">
          <DropdownField
            label="Type"
            value={form.type}
            onChange={(v: string) => update({ type: v as EntrypointType })}
            options={TYPE_OPTIONS}
          />
        </View>
        <View className="flex-1">
          <DropdownField
            label="Status"
            value={form.status}
            onChange={(v: string) => update({ status: v as EntrypointStatus })}
            options={STATUS_OPTIONS}
          />
        </View>
      </View>
      {form.type === "phone" && (
        <>
          {phoneNumberOptions.length === 0 ? (
            <View className="flex flex-col gap-1">
              <Text className="text-xs font-medium text-gray-700 dark:text-gray-300">Phone Number</Text>
              <Text className="text-xs text-gray-400 dark:text-gray-500">No unassigned phone numbers available. Add one under Phone Numbers.</Text>
            </View>
          ) : (
            <DropdownField
              label="Phone Number"
              value={form.phoneNumber}
              onChange={(v: string) => update({ phoneNumber: v })}
              options={phoneNumberOptions}
            />
          )}
          <InputField
            label="Friendly Name"
            value={form.friendlyName}
            onChange={(v: string) => update({ friendlyName: v })}
            placeholder="Optional"
          />
          <DropdownField
            label="Direction"
            value={form.direction}
            onChange={(v: string) => update({ direction: v as FormState["direction"] })}
            options={DIRECTION_OPTIONS}
          />
        </>
      )}
      {form.type === "web" && (
        <InputField
          label="Allowed Origins"
          value={form.allowedOrigins}
          onChange={(v: string) => update({ allowedOrigins: v })}
          placeholder="https://example.com, https://app.example.com"
        />
      )}
      {form.type === "native" && (
        <InputField
          label="Client ID"
          value={form.clientId}
          onChange={(v: string) => update({ clientId: v })}
          placeholder="my-native-client"
        />
      )}
    </>
  );
};

type RoutingDisplayProps = {
  entrypoint: Entrypoint;
  releases: Release[];
  environments: Environment[];
};

const RoutingDisplay = ({ entrypoint, releases, environments }: RoutingDisplayProps) => {
  if (!entrypoint.releases || entrypoint.releases.length === 0) {
    return <Text className="text-[11px] text-gray-400 dark:text-gray-500">No releases routed</Text>;
  }
  const totalWeight = entrypoint.releases.reduce((acc, wr) => acc + (wr.weight || 0), 0) || 1;
  return (
    <View className="flex flex-col gap-1">
      {entrypoint.releases.map((wr) => {
        const release = releases.find((r) => r.id === wr.releaseId);
        const env = release ? environments.find((e) => e.id === release.environmentId) : undefined;
        const label = env?.name || release?.id || wr.releaseId;
        const shortId = wr.releaseId.slice(0, 8);
        const percent = Math.round((wr.weight / totalWeight) * 100);
        return (
          <View
            key={wr.releaseId}
            className="flex flex-row items-center justify-between gap-2 text-[11px]"
          >
            <View className="flex flex-row items-center gap-1.5 min-w-0">
              <Icon
                name="arrow-right"
                size={10}
                className="text-gray-400 shrink-0"
              />
              <Text className="text-gray-700 dark:text-gray-300 truncate">{label}</Text>
              <Text className="font-mono text-gray-400 dark:text-gray-500 shrink-0">{shortId}</Text>
            </View>
            <Text className="text-gray-500 dark:text-gray-400 shrink-0">{percent}%</Text>
          </View>
        );
      })}
    </View>
  );
};

type OrganizationEntrypointsCardProps = {
  entrypoints: Entrypoint[];
  releases: Release[];
  environments: Environment[];
  organizationPhoneNumbers: OrganizationPhoneNumber[];
  onAdd: (data: EntrypointInput) => void;
  onUpdate: (id: string, data: Partial<EntrypointInput>) => void;
  onDelete: (id: string) => Promise<void>;
};

export const OrganizationEntrypointsCard = ({
  entrypoints,
  releases,
  environments,
  organizationPhoneNumbers,
  onAdd,
  onUpdate,
  onDelete,
}: OrganizationEntrypointsCardProps) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleAdd = () => {
    if (!isFormValid(addForm)) return;
    onAdd({
      name: addForm.name.trim(),
      type: addForm.type,
      status: addForm.status,
      config: buildConfig(addForm),
    });
    setAddForm(emptyForm);
    setShowAddForm(false);
  };

  const handleStartEdit = (entrypoint: Entrypoint) => {
    setEditingId(entrypoint.id);
    setEditForm(formFromEntrypoint(entrypoint));
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyForm);
  };

  const handleSaveEdit = (id: string) => {
    if (!isFormValid(editForm)) return;
    onUpdate(id, {
      name: editForm.name.trim(),
      type: editForm.type,
      status: editForm.status,
      config: buildConfig(editForm),
    });
    handleCancelEdit();
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await onDelete(deleteId);
      setDeleteId(null);
    }
  };

  const sortedEntrypoints = [...entrypoints].sort((a, b) => a.name.localeCompare(b.name));
  const entrypointToDelete = entrypoints.find((e) => e.id === deleteId);

  const usedPhoneNumbers = new Set(
    entrypoints
      .filter((e) => e.type === "phone")
      .map((e) => (e.config as PhoneEntrypointConfig).phoneNumber)
      .filter(Boolean),
  );

  const phoneOptionFor = (number: OrganizationPhoneNumber) => ({
    label: number.friendlyName ? `${number.phoneNumber} — ${number.friendlyName}` : number.phoneNumber,
    value: number.phoneNumber,
  });

  const buildPhoneOptions = (currentValue: string) =>
    organizationPhoneNumbers.filter((n) => !n.agentId && (!usedPhoneNumbers.has(n.phoneNumber) || n.phoneNumber === currentValue)).map(phoneOptionFor);

  const addPhoneOptions = buildPhoneOptions(addForm.phoneNumber);
  const editPhoneOptions = buildPhoneOptions(editForm.phoneNumber);

  return (
    <OrganizationSettingsTab
      title="Entrypoints"
      description="Inbound traffic sources (phone numbers, web widgets, native clients) that route callers to releases. Routing weights are managed on the agent's releases page."
      count={entrypoints.length}
    >
      <View className="flex flex-col gap-3">
        {sortedEntrypoints.map((entrypoint) => (
          <Card
            key={entrypoint.id}
            className="p-4"
          >
            {editingId === entrypoint.id ? (
              <View className="flex flex-col gap-3">
                <EntrypointFormFields
                  form={editForm}
                  setForm={setEditForm}
                  phoneNumberOptions={editPhoneOptions}
                />
                <View className="flex flex-row gap-2 justify-end">
                  <Button
                    variant="neutral"
                    size="sm"
                    outline
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    outline
                    onClick={() => handleSaveEdit(entrypoint.id)}
                    disabled={!isFormValid(editForm)}
                  >
                    <Icon
                      name="check"
                      size={14}
                    />
                    Save
                  </Button>
                </View>
              </View>
            ) : (
              <View className="flex flex-col gap-3">
                <View className="flex flex-row items-center justify-between gap-3">
                  <View className="flex flex-row items-center gap-3 min-w-0">
                    <Icon
                      name={ENTRYPOINT_TYPE_ICON[entrypoint.type]}
                      size={16}
                      className="text-gray-400 shrink-0"
                    />
                    <View className="flex flex-col min-w-0">
                      <View className="flex flex-row items-center gap-2 min-w-0">
                        <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{entrypoint.name}</Text>
                        <Text
                          className={
                            entrypoint.status === "active"
                              ? "text-[10px] font-medium uppercase tracking-wider text-green-600 dark:text-green-400 shrink-0"
                              : "text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 shrink-0"
                          }
                        >
                          {capitalize(entrypoint.status)}
                        </Text>
                      </View>
                      <View className="flex flex-row items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 truncate">
                        <Text className="capitalize">{entrypoint.type}</Text>
                        {summarizeConfig(entrypoint) && (
                          <>
                            <Text>·</Text>
                            <Text className="truncate">{summarizeConfig(entrypoint)}</Text>
                          </>
                        )}
                      </View>
                    </View>
                  </View>
                  <View className="flex flex-row items-center gap-2 shrink-0">
                    <Button
                      variant="neutral"
                      size="sm"
                      outline
                      onClick={() => handleStartEdit(entrypoint)}
                      className="h-8"
                    >
                      <Icon
                        name="edit"
                        size={14}
                      />
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      outline
                      onClick={() => setDeleteId(entrypoint.id)}
                      className="h-8"
                      aria-label="Delete"
                    >
                      <Icon
                        name="trash"
                        size={14}
                      />
                    </Button>
                  </View>
                </View>
                <View className="flex flex-col gap-1.5 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                  <Text className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Routing</Text>
                  <RoutingDisplay
                    entrypoint={entrypoint}
                    releases={releases}
                    environments={environments}
                  />
                </View>
              </View>
            )}
          </Card>
        ))}

        {showAddForm ? (
          <Card className="p-4 border-dashed">
            <View className="flex flex-col gap-3">
              <EntrypointFormFields
                form={addForm}
                setForm={setAddForm}
                phoneNumberOptions={addPhoneOptions}
              />
              <View className="flex flex-row gap-2 justify-end">
                <Button
                  variant="neutral"
                  size="sm"
                  outline
                  onClick={() => {
                    setShowAddForm(false);
                    setAddForm(emptyForm);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  outline
                  onClick={handleAdd}
                  disabled={!isFormValid(addForm)}
                >
                  <Icon
                    name="add"
                    size={14}
                  />
                  Add Entrypoint
                </Button>
              </View>
            </View>
          </Card>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex flex-row items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-lg px-3 py-2.5 group border border-dashed border-gray-300 dark:border-gray-600"
          >
            <View className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={13}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Add entrypoint
            </Text>
          </button>
        )}
      </View>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Entrypoint"
        message={
          entrypointToDelete?.name
            ? `Delete "${entrypointToDelete.name}"? Releases routing through this entrypoint will lose this traffic path.`
            : "Delete this entrypoint?"
        }
        confirmLabel="Delete"
        variant="danger"
      />
    </OrganizationSettingsTab>
  );
};
